import React, { useState, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import "./PartyOrder.scss";
import {
  collection,
  getDocs,
  query,
  orderBy,
  doc,
  updateDoc,
  deleteDoc,
  arrayUnion,
} from "firebase/firestore";
import { db, storage } from "../../../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

const STATUS_OPTIONS = [
  "Pending",
  "Processing",
  "Polishing",
  "Complete",
  "Hallmarked",
  "Delivered",
];

const normalizeStatus = (s) =>
  s
    ? String(s).trim().charAt(0).toUpperCase() +
      String(s).trim().slice(1).toLowerCase()
    : "Pending";

const getPartyName = (id, parties) =>
  parties.find((p) => p.id === id)?.name || `ID: ${id || "N/A"}`;

const getOrderDate = (o) => {
  if (!o.createdAt) return new Date(o.deliveryDate || Date.now());
  if (o.createdAt.seconds) return new Date(o.createdAt.seconds * 1000);
  if (o.createdAt.toDate) return o.createdAt.toDate();
  return new Date(o.createdAt);
};

// =========================================================================
// 🔥 NEW: ENTERPRISE FINANCIAL YEAR & SERIES GENERATOR
// =========================================================================
const getOrderFY = (dateObj) => {
  if (isNaN(dateObj.getTime())) return "FY-XX";
  const year = dateObj.getFullYear();
  const month = dateObj.getMonth(); // 0 = Jan, 3 = Apr
  // Indian Financial Year: April 1st to March 31st
  if (month >= 3) {
    return `FY${year.toString().slice(-2)}-${(year + 1).toString().slice(-2)}`;
  } else {
    return `FY${(year - 1).toString().slice(-2)}-${year.toString().slice(-2)}`;
  }
};

const getFilingSeries = (order) => {
  const dateObj = getOrderDate(order);
  const fy = getOrderFY(dateObj);
  const rawId = String(order.orderId || order.id || "");

  // If the ID is a sequential number (e.g., "45"), pad it to "0045"
  // If it's a long alphanumeric Firebase ID, extract the last 5 characters
  const isNumeric = /^\d+$/.test(rawId);
  let seriesNum = "";

  if (isNumeric) {
    seriesNum = rawId.padStart(4, "0");
  } else {
    seriesNum = rawId.slice(-5).toUpperCase();
  }

  return `${fy} / ${seriesNum}`;
};

// Extractor for a single thumbnail
const extractItemImage = (item) => {
  if (item?.fileUrls && item.fileUrls.length > 0) return item.fileUrls[0];
  if (item?.fileUrl) return item.fileUrl;
  return null;
};

// Extractor for ALL images associated with an item
const extractAllItemImages = (item) => {
  if (
    item?.fileUrls &&
    Array.isArray(item.fileUrls) &&
    item.fileUrls.length > 0
  ) {
    return item.fileUrls;
  }
  if (item?.fileUrl) {
    return [item.fileUrl];
  }
  return [];
};

const extractMasterThumbnail = (order) => {
  if (order.files && order.files.length > 0)
    return order.files[order.files.length - 1]; // Prioritize newest edit
  if (order.orderType === "Single" && order.singleItems?.length > 0)
    return extractItemImage(order.singleItems[0]);
  if (order.items?.length > 0) return extractItemImage(order.items[0]);
  return null;
};

const getBulkItemDetailString = (item, order) => {
  if (item.beadSize)
    return `${order.subType || "Bulk"} - ${item.beadSize} / ${item.length}`;
  if (item.weightRange)
    return `${item.type || order.subType || "Watti Mani"} - ${item.weightRange}`;
  const prodName =
    item.productName ||
    item.product ||
    item.category ||
    order.subType ||
    "Bulk Item";
  const sizeInfo = [
    item.size,
    item.length ? `${item.length} ${item.lengthUnit || ""}`.trim() : null,
  ]
    .filter(Boolean)
    .join(" / ");
  return sizeInfo ? `${prodName} - ${sizeInfo}` : prodName;
};

// =========================================================================
// 🔥 UI/UX UPGRADED JOB CARD (Strict A4, Fixed Images, FY Series Barcode)
// =========================================================================
const triggerPrintJobCard = (order, partyName) => {
  const printWindow = window.open("", "", "height=1000,width=850");
  let allImages = [];

  if (order.files) allImages.push(...order.files);
  if (order.singleItems) {
    order.singleItems.forEach((item) => {
      allImages.push(...extractAllItemImages(item));
    });
  } else if (order.items) {
    order.items.forEach((item) => {
      allImages.push(...extractAllItemImages(item));
    });
  }

  // De-duplicate array and limit to max 5 to prevent A4 overflow
  allImages = [...new Set(allImages)].slice(0, 5);

  const issueDate = new Date().toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  const orderDateObj = getOrderDate(order);
  const orderDateFormatted = orderDateObj.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const filingSeries = getFilingSeries(order);

  // Scannable Barcode URL encodes the exact DB orderId, but visual text shows the clean series
  const barcodeUrl = `https://barcode.tec-it.com/barcode.ashx?data=${encodeURIComponent(order.orderId)}&code=Code128&translate-esc=on`;

  const htmlContent = `
    <html><head><title>Job Card - ${filingSeries}</title>
    <style>
      /* Base A4 Setup */
      body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; font-size: 10pt; margin: 0; padding: 0; color: #000; background: #fff; }
      .job-work-card { width: 210mm; min-height: 297mm; margin: 0 auto; padding: 10mm; box-sizing: border-box; }
      
      /* Header Design */
      .erp-header { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 2px solid #000; padding-bottom: 12px; margin-bottom: 15px; }
      .erp-header-left { flex: 1; }
      .erp-header-center { flex: 1.5; text-align: center; }
      .erp-header-right { flex: 1; display: flex; justify-content: flex-end; }
      
      .company-tag { font-size: 0.85em; color: #555; text-transform: uppercase; letter-spacing: 1px; }
      .doc-title { font-size: 1.8em; font-weight: 900; margin: 6px 0; letter-spacing: 1px; }
      
      /* The new Filing Badge with Barcode and Series Num */
      .filing-badge { border: 2px solid #000; border-radius: 6px; padding: 6px 12px; display: inline-flex; flex-direction: column; align-items: center; background: #fff; }
      .barcode-img { height: 38px; margin-bottom: 6px; }
      .filing-series { font-size: 1.3em; font-weight: 900; letter-spacing: 1px; border-top: 1px dashed #ccc; padding-top: 6px; width: 100%; text-align: center; }
      
      /* Meta Grids */
      .print-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 15px; margin-bottom: 15px; }
      .meta-box { border: 1px solid #000; padding: 8px 10px; border-radius: 4px; }
      .meta-box h2 { font-size: 0.8em; text-transform: uppercase; color: #555; border-bottom: 1px solid #ccc; padding-bottom: 3px; margin-top: 0; margin-bottom: 6px; }
      .data-row { display: flex; justify-content: space-between; padding: 3px 0; font-size: 0.9em; }
      .data-row span:first-child { font-weight: bold; }
      
      /* Target Tables (Includes manual write-in columns) */
      .section-title { font-weight: bold; margin-bottom: 6px; text-transform: uppercase; font-size: 0.9em; }
      .data-table { width: 100%; border-collapse: collapse; font-size: 0.85em; margin-bottom: 15px; }
      .data-table th, .data-table td { border: 1px solid #000; padding: 8px 6px; text-align: left; vertical-align: middle; }
      .data-table th { background-color: #f1f5f9; font-weight: bold; text-transform: uppercase; font-size: 0.8em; }
      
      /* Manual Entry Columns Layout */
      .write-col { width: 13%; text-align: center; }
      .data-table td.write-box { height: 35px; } 
      
      /* Fixed Image Grid */
      .img-gallery { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 15px; }
      .img-box { width: 130px; height: 130px; border: 1px solid #94a3b8; border-radius: 6px; padding: 4px; display: flex; justify-content: center; align-items: center; background: #fff; }
      .img-box img { max-width: 100%; max-height: 100%; object-fit: contain; }
      
      /* Notes Box */
      .notes-box { border: 1px dashed #64748b; border-radius: 6px; padding: 10px; min-height: 80px; margin-bottom: 15px; background: #fafafa; }
      .notes-box h2 { font-size: 0.8em; margin: 0 0 5px 0; text-transform: uppercase; color: #555; }
      
      /* Routing Tracker */
      .routing-table td { height: 40px; } 

      /* Signatures */
      .signature-row { display: flex; justify-content: space-between; margin-top: 40px; font-size: 0.9em; }
      .sign-box { border-top: 1px solid #000; width: 28%; text-align: center; padding-top: 5px; }
      
      @media print { 
        @page { size: A4; margin: 0; } 
        body { -webkit-print-color-adjust: exact; } 
      }
    </style></head><body>
    <div class="job-work-card">
      
      <!-- ERP HEADER -->
      <div class="erp-header">
        <div class="erp-header-left">
          <div class="company-tag">INTERNAL DOCUMENT</div>
          <div style="margin-top: 8px;"><strong>Date:</strong> ${orderDateFormatted}</div>
          <div><strong>Delivery:</strong> <span style="font-size: 1.1em; font-weight: bold; color: #d92626;">${order.deliveryDate}</span></div>
        </div>
        <div class="erp-header-center">
          <h1 class="doc-title">JOB CARD</h1>
        </div>
        <div class="erp-header-right">
          <!-- The Upgraded Sequential Filing Badge -->
          <div class="filing-badge">
            <img src="${barcodeUrl}" alt="Barcode" class="barcode-img" />
            <div class="filing-series">${filingSeries}</div>
          </div>
        </div>
      </div>

      <!-- MASTER INFO -->
      <div class="print-grid-3">
        <div class="meta-box"><h2>Party Details</h2>
          <div class="data-row"><span>Account:</span><span>${partyName}</span></div>
          <div class="data-row"><span>Printed:</span><span>${issueDate}</span></div>
        </div>
        <div class="meta-box"><h2>Classification</h2>
          <div class="data-row"><span>Type:</span><span>${order.orderType}</span></div>
          ${order.orderType === "Single" ? `<div class="data-row"><span>Product:</span><span>${order.product}</span></div>` : ""}
          ${order.orderType === "Bulk" ? `<div class="data-row"><span>Sub-Type:</span><span>${order.subType}</span></div>` : ""}
        </div>
        <div class="meta-box"><h2>Material</h2>
          <div class="data-row"><span>Metal:</span><span>Gold</span></div>
          <div class="data-row"><span>Purity:</span><span>__ K</span></div>
        </div>
      </div>

      <!-- PRODUCTION DETAILS & WEIGHT WRITING COLUMNS -->
      <div class="section-title">Production Requirements & Weight Log:</div>
      ${
        order.orderType === "Single"
          ? `
        <table class="data-table">
          <thead>
            <tr>
              <th style="width: 5%; text-align: center;">Sr</th>
              <th style="width: 35%;">Item Details & Dimensions</th>
              <th style="width: 15%;">Target Wt</th>
              <th style="width: 6%; text-align: center;">Qty</th>
              <th class="write-col">Gross Wt.</th>
              <th class="write-col">BB Wt.</th>
              <th class="write-col">Net Wt.</th>
            </tr>
          </thead>
          <tbody>
            ${order.singleItems
              .map(
                (row, idx) => `
              <tr>
                <td style="text-align: center;">${idx + 1}</td>
                <td><strong>Main:</strong> ${row.length} ${row.lengthUnit} ${row.pendantLength ? `<br/><strong>Pendant:</strong> ${row.pendantLength} ${row.pendantLengthUnit}` : ""}</td>
                <td>
                  <div>${row.weight}g ${row.pendantWeight ? `+ ${row.pendantWeight}g Pend` : ""}</div>
                </td>
                <td style="font-weight: bold; text-align: center; font-size: 1.2em;">${row.quantity}</td>
                <td class="write-box"></td>
                <td class="write-box"></td>
                <td class="write-box"></td>
              </tr>
            `,
              )
              .join("")}
          </tbody>
        </table>
      `
          : order.orderType === "Bulk"
            ? `
        <table class="data-table">
          <thead>
            <tr>
              <th style="width: 5%; text-align: center;">Sr</th>
              <th style="width: 35%;">Description</th>
              <th style="width: 15%;">Est. Wt</th>
              <th style="width: 6%; text-align: center;">Qty</th>
              <th class="write-col">Gross Wt.</th>
              <th class="write-col">BB Wt.</th>
              <th class="write-col">Net Wt.</th>
            </tr>
          </thead>
          <tbody>
            ${order.items
              .map(
                (row, idx) => `
              <tr>
                <td style="text-align: center;">${idx + 1}</td>
                <td><strong>${getBulkItemDetailString(row, order)}</strong></td>
                <td>${row.weight || row.grossWeight || "-"}</td>
                <td style="font-weight: bold; text-align: center; font-size: 1.2em;">${row.quantity || row.qty || 1}</td>
                <td class="write-box"></td>
                <td class="write-box"></td>
                <td class="write-box"></td>
              </tr>
            `,
              )
              .join("")}
          </tbody>
        </table>
      `
            : `
        <table class="data-table">
          <thead>
            <tr><th>Carat</th><th>Est Weight</th><th>Stone Specs</th><th class="write-col">Gross Wt.</th><th class="write-col">Net Wt.</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>${order.castingDetails?.carat}</td>
              <td>${order.castingDetails?.weight}</td>
              <td>${order.castingDetails?.stone}</td>
              <td class="write-box"></td>
              <td class="write-box"></td>
            </tr>
          </tbody>
        </table>
      `
      }

      <!-- FIXED SIZE IMAGE GALLERY -->
      ${
        allImages.length > 0
          ? `
      <div class="section-title">Design References:</div>
      <div class="img-gallery">
        ${allImages.map((url) => `<div class="img-box"><img src="${url}" /></div>`).join("")}
      </div>`
          : ""
      }

      <!-- SPECIAL INSTRUCTIONS & NOTES AREA -->
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px;">
        <div class="meta-box" style="border-color: #000; background: #fff; min-height: 80px;">
          <h2>Special Instructions</h2>
          <p style="margin: 0; font-weight: bold;">${order.comments || "Standard Procedure"}</p>
        </div>
        <div class="notes-box">
          <h2>Workshop Calculations / Notes</h2>
        </div>
      </div>

      <!-- FACTORY ROUTING TRACKER -->
      <div style="margin-top: 15px;">
        <div class="section-title">Routing & Metal Log:</div>
        <table class="data-table routing-table">
          <thead>
            <tr>
              <th style="width: 15%;">Process</th>
              <th style="width: 25%;">Karigar Name</th>
              <th style="width: 15%;">Issue Wt.</th>
              <th style="width: 15%;">Recv Wt.</th>
              <th style="width: 15%;">Scrap/Loss</th>
              <th style="width: 15%;">Sign</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Melting/Wire</strong></td><td></td><td></td><td></td><td></td><td></td></tr>
            <tr><td><strong>Making</strong></td><td></td><td></td><td></td><td></td><td></td></tr>
            <tr><td><strong>Setting/Pol</strong></td><td></td><td></td><td></td><td></td><td></td></tr>
          </tbody>
        </table>
      </div>

      <!-- QC SIGNATURES -->
      <div class="signature-row">
        <div class="sign-box">Manager Issue Sign</div>
        <div class="sign-box">Karigar Check</div>
        <div class="sign-box">QC/Vault Receive Sign</div>
      </div>

    </div>
    </body></html>`;

  printWindow.document.write(htmlContent);
  printWindow.document.close();
  // Wait slightly longer for images & barcode to load fully
  setTimeout(() => printWindow.print(), 1200);
};

const triggerPrintPendingList = (orders, parties) => {
  const pendingOrders = orders.filter(
    (o) =>
      !["Complete", "Hallmarked", "Delivered"].includes(
        normalizeStatus(o.status),
      ) && o.orderType !== "Bulk",
  );
  const printWindow = window.open("", "", "height=800,width=1100");

  const htmlContent = `
    <html><head><title>Pending Orders List</title>
    <style>
      body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; font-size: 10pt; padding: 20px; color: #222; }
      h1 { text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 10px; text-transform: uppercase; letter-spacing: 1px; }
      .meta { text-align: right; font-size: 9pt; color: #555; margin-bottom: 15px; }
      table { width: 100%; border-collapse: collapse; margin-top: 10px; }
      th, td { border: 1px solid #000; padding: 12px; text-align: left; vertical-align: middle; }
      th { background-color: #f8fafc; font-weight: bold; text-transform: uppercase; font-size: 9pt; color: #334155; }
      img { width: 75px; height: 75px; object-fit: cover; border-radius: 6px; border: 1px solid #cbd5e1; display: block; margin: 0 auto; }
      .notes-space { min-height: 75px; }
      .party-name { font-size: 11pt; font-weight: bold; color: #0f172a; display: block; margin-bottom: 4px; }
      .date { font-size: 9.5pt; color: #dc2626; font-weight: bold; }
      .item-row { margin-bottom: 4px; font-size: 9.5pt; color: #1e293b; }
      .type-label { font-size: 8pt; text-transform: uppercase; color: #64748b; border-bottom: 1px solid #e2e8f0; padding-bottom: 4px; margin-bottom: 6px; display: block; }
      .instructions { font-size: 9pt; color: #b45309; background: #fef3c7; padding: 6px; border-radius: 4px; margin-bottom: 8px; border: 1px solid #fde68a; }
      @media print { 
        @page { size: A4 landscape; margin: 10mm; } 
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        table { page-break-inside: auto; }
        tr { page-break-inside: avoid; page-break-after: auto; }
      }
    </style></head><body>
    <h1>Pending Production Orders</h1>
    <div class="meta">Generated: ${new Date().toLocaleString()}</div>
    <table>
      <thead>
        <tr>
          <th style="width: 5%; text-align: center;">Sr</th>
          <th style="width: 20%;">Party & Delivery</th>
          <th style="width: 35%;">Order Details</th>
          <th style="width: 15%; text-align: center;">Reference</th>
          <th style="width: 25%;">Instructions / Notes</th>
        </tr>
      </thead>
      <tbody>
        ${pendingOrders
          .map((o, idx) => {
            const thumb = extractMasterThumbnail(o);
            const pName = getPartyName(o.partyId, parties);
            let detailsHtml = "";
            if (o.orderType === "Single") {
              detailsHtml =
                o.singleItems
                  ?.map(
                    (i) =>
                      `<div class="item-row">• <strong>${i.weight}g</strong> | ${i.length}${i.lengthUnit} (${i.quantity} pcs)</div>`,
                  )
                  .join("") || "-";
            } else if (o.orderType === "Casting") {
              detailsHtml = `<div class="item-row">• ${o.castingDetails?.item} | <strong>${o.castingDetails?.weight}</strong> | ${o.castingDetails?.carat}</div>`;
            }
            return `
          <tr>
            <td style="text-align: center; font-weight: bold; font-size: 11pt;">${idx + 1}</td>
            <td><span class="party-name">${pName}</span><span class="date">Due: ${o.deliveryDate || "N/A"}</span><br/><span style="font-size:8pt; color:#64748b; margin-top: 4px; display: block;">ID: #${o.orderId}</span></td>
            <td><span class="type-label">${o.orderType} - ${o.product || o.subType || "Items"}</span>${detailsHtml}</td>
            <td>${thumb ? `<img src="${thumb}"/>` : `<div style="text-align:center; color:#94a3b8; font-size:8pt;">No Img</div>`}</td>
            <td>
              ${o.comments ? `<div class="instructions"><strong>📝 Note:</strong> ${o.comments}</div>` : ""}
              <div class="notes-space"></div>
            </td>
          </tr>`;
          })
          .join("")}
      </tbody>
    </table>
    </body></html>`;
  printWindow.document.write(htmlContent);
  printWindow.document.close();
  setTimeout(() => printWindow.print(), 1000);
};

const openWhatsAppReport = (order, partyName) => {
  let text = `*📊 Order Status Report*\n\n*Party:* ${partyName}\n*Order ID:* #${order.orderId}\n*Date:* ${new Date().toLocaleDateString()}\n\n`;

  if (order.orderType === "Single") {
    const completed = order.singleItems.filter(
      (i) => normalizeStatus(i.itemStatus) === "Delivered",
    );
    const pending = order.singleItems.filter(
      (i) => normalizeStatus(i.itemStatus) !== "Delivered",
    );

    if (completed.length > 0) {
      text += `✅ *COMPLETED ITEMS:*\n`;
      completed.forEach((i) => {
        text += `- ${i.weight}g | ${i.length} ${i.lengthUnit} (${i.quantity} pcs)\n`;
      });
      text += `\n`;
    }
    if (pending.length > 0) {
      text += `⏳ *IN PRODUCTION (Running):*\n`;
      pending.forEach((i) => {
        text += `- ${i.weight}g | ${i.length} ${i.lengthUnit} (${i.quantity} pcs) - *[${normalizeStatus(i.itemStatus)}]*\n`;
        const itemImgs = extractAllItemImages(i);
        itemImgs.forEach((img, index) => {
          text += `  🖼️ Ref ${itemImgs.length > 1 ? index + 1 : ""}: ${img}\n`;
        });
      });
    }
  } else {
    text += `*Current Status:* ${normalizeStatus(order.status)}\n`;
  }
  window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
};

// ==========================================
// 1. FINALIZATION MODAL (Step-By-Step Loop)
// ==========================================
const FinalizeOrderModal = ({
  order,
  partyName,
  itemIndexes,
  intendedMasterStatus,
  intendedItemStatus,
  onClose,
  onSaveMultiple,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [collectedData, setCollectedData] = useState({});
  const [formData, setFormData] = useState({});

  const currentIndex = itemIndexes[currentStep];
  const isBulk = currentIndex === -1;
  const itemTarget = isBulk ? order : order.singleItems[currentIndex];

  useEffect(() => {
    const fd = itemTarget?.finalizationData || {};
    setFormData({
      grossWeight: fd.grossWeight || "",
      netWeight: fd.netWeight || "",
      bbWeight: fd.bbWeight || "",
      bbCount: fd.bbCount || "",
      potaStoneWeight: fd.potaStoneWeight || "",
      goldManiList: fd.goldManiList?.length
        ? fd.goldManiList
        : [{ size: "", weight: "" }],
      goldPcsList: fd.goldPcsList?.length
        ? fd.goldPcsList
        : [{ length: "", weight: "" }],
      msPcsList: fd.msPcsList?.length
        ? fd.msPcsList
        : [{ length: "", weight: "" }],
      pendantWeight: fd.pendantWeight || "",
      pendantSize: fd.pendantSize || "",
      remarks: fd.remarks || "",
    });
  }, [currentStep, itemTarget]);

  const handleNextOrSave = (e) => {
    e.preventDefault();
    if (!formData.grossWeight || !formData.netWeight)
      return alert("Gross Weight and Net Weight are mandatory.");

    const newCollectedMap = { ...collectedData, [currentIndex]: formData };
    setCollectedData(newCollectedMap);

    if (currentStep < itemIndexes.length - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      onSaveMultiple(
        order.id,
        intendedMasterStatus,
        intendedItemStatus,
        newCollectedMap,
        itemIndexes,
      );
    }
  };

  return createPortal(
    <div className="erp-modal-overlay erp-fade-in" style={{ zIndex: 999999 }}>
      <form
        className="erp-modern-modal final-modal-card erp-pop-in"
        onSubmit={handleNextOrSave}
      >
        <div className="modal-header fm-header">
          <div className="header-info">
            <h2>
              {itemTarget?.finalizationData
                ? "📝 Edit Final Metrics"
                : "🔒 Finalize Order Metrics"}
            </h2>
            <div
              style={{
                color: "#60a5fa",
                fontWeight: "bold",
                margin: "6px 0",
                fontSize: "0.9rem",
              }}
            >
              Order: #{order.orderId} | Party: {partyName}
            </div>
            <span>
              {!isBulk && itemIndexes.length > 1
                ? `Recording Sub-Item ${currentStep + 1} of ${itemIndexes.length} | Specs: ${itemTarget?.weight}g`
                : `Record final production metrics for this item.`}
            </span>
          </div>
          <button type="button" className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-body fm-body">
          <div className="fm-grid-2">
            <div className="erp-input-group">
              <label>
                Gross Weight <span>*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. 50g"
                value={formData.grossWeight}
                onChange={(e) =>
                  setFormData({ ...formData, grossWeight: e.target.value })
                }
                required
              />
            </div>
            <div className="erp-input-group">
              <label>
                Net Weight <span>*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. 48.5g"
                value={formData.netWeight}
                onChange={(e) =>
                  setFormData({ ...formData, netWeight: e.target.value })
                }
                required
              />
            </div>
            <div className="erp-input-group">
              <label>BB Weight</label>
              <input
                type="text"
                placeholder="e.g. 1.5g"
                value={formData.bbWeight}
                onChange={(e) =>
                  setFormData({ ...formData, bbWeight: e.target.value })
                }
              />
            </div>
            <div className="erp-input-group">
              <label>BB Count</label>
              <input
                type="text"
                placeholder="e.g. 24"
                value={formData.bbCount}
                onChange={(e) =>
                  setFormData({ ...formData, bbCount: e.target.value })
                }
              />
            </div>
            <div className="erp-input-group">
              <label>Pota/Stone Weight</label>
              <input
                type="text"
                placeholder="e.g. 0.8g"
                value={formData.potaStoneWeight}
                onChange={(e) =>
                  setFormData({ ...formData, potaStoneWeight: e.target.value })
                }
              />
            </div>
          </div>

          <hr style={{ borderColor: "#1e293b", margin: "20px 0" }} />

          <div className="fm-dynamic-list">
            <h4>Gold Mani</h4>
            {formData.goldManiList?.map((item, idx) => (
              <div className="row" key={`gm-${idx}`}>
                <input
                  type="text"
                  placeholder="Degree / Size"
                  value={item.size}
                  onChange={(e) => {
                    const n = [...formData.goldManiList];
                    n[idx].size = e.target.value;
                    setFormData({ ...formData, goldManiList: n });
                  }}
                />
                <input
                  type="text"
                  placeholder="Weight"
                  value={item.weight}
                  onChange={(e) => {
                    const n = [...formData.goldManiList];
                    n[idx].weight = e.target.value;
                    setFormData({ ...formData, goldManiList: n });
                  }}
                />
                {formData.goldManiList.length > 1 && (
                  <button
                    type="button"
                    className="btn-del"
                    onClick={() => {
                      const n = formData.goldManiList.filter(
                        (_, i) => i !== idx,
                      );
                      setFormData({ ...formData, goldManiList: n });
                    }}
                  >
                    X
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              className="btn-add"
              onClick={() =>
                setFormData({
                  ...formData,
                  goldManiList: [
                    ...formData.goldManiList,
                    { size: "", weight: "" },
                  ],
                })
              }
            >
              + Add Gold Mani
            </button>
          </div>

          <div className="fm-dynamic-list">
            <h4>Gold Pcs</h4>
            {formData.goldPcsList?.map((item, idx) => (
              <div className="row" key={`gp-${idx}`}>
                <input
                  type="text"
                  placeholder="Length"
                  value={item.length}
                  onChange={(e) => {
                    const n = [...formData.goldPcsList];
                    n[idx].length = e.target.value;
                    setFormData({ ...formData, goldPcsList: n });
                  }}
                />
                <input
                  type="text"
                  placeholder="Weight"
                  value={item.weight}
                  onChange={(e) => {
                    const n = [...formData.goldPcsList];
                    n[idx].weight = e.target.value;
                    setFormData({ ...formData, goldPcsList: n });
                  }}
                />
                {formData.goldPcsList.length > 1 && (
                  <button
                    type="button"
                    className="btn-del"
                    onClick={() => {
                      const n = formData.goldPcsList.filter(
                        (_, i) => i !== idx,
                      );
                      setFormData({ ...formData, goldPcsList: n });
                    }}
                  >
                    X
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              className="btn-add"
              onClick={() =>
                setFormData({
                  ...formData,
                  goldPcsList: [
                    ...formData.goldPcsList,
                    { length: "", weight: "" },
                  ],
                })
              }
            >
              + Add Gold Pcs
            </button>
          </div>

          <div className="fm-dynamic-list">
            <h4>Mangalsutra Pcs</h4>
            {formData.msPcsList?.map((item, idx) => (
              <div className="row" key={`ms-${idx}`}>
                <input
                  type="text"
                  placeholder="Length"
                  value={item.length}
                  onChange={(e) => {
                    const n = [...formData.msPcsList];
                    n[idx].length = e.target.value;
                    setFormData({ ...formData, msPcsList: n });
                  }}
                />
                <input
                  type="text"
                  placeholder="Weight"
                  value={item.weight}
                  onChange={(e) => {
                    const n = [...formData.msPcsList];
                    n[idx].weight = e.target.value;
                    setFormData({ ...formData, msPcsList: n });
                  }}
                />
                {formData.msPcsList.length > 1 && (
                  <button
                    type="button"
                    className="btn-del"
                    onClick={() => {
                      const n = formData.msPcsList.filter((_, i) => i !== idx);
                      setFormData({ ...formData, msPcsList: n });
                    }}
                  >
                    X
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              className="btn-add"
              onClick={() =>
                setFormData({
                  ...formData,
                  msPcsList: [
                    ...formData.msPcsList,
                    { length: "", weight: "" },
                  ],
                })
              }
            >
              + Add Mangalsutra Pcs
            </button>
          </div>

          <hr style={{ borderColor: "#1e293b", margin: "20px 0" }} />

          <div className="fm-grid-2">
            <div className="erp-input-group">
              <label>Pendant Weight</label>
              <input
                type="text"
                placeholder="e.g. 10g"
                value={formData.pendantWeight}
                onChange={(e) =>
                  setFormData({ ...formData, pendantWeight: e.target.value })
                }
              />
            </div>
            <div className="erp-input-group">
              <label>Pendant Size</label>
              <input
                type="text"
                placeholder="e.g. 2.5 Inches"
                value={formData.pendantSize}
                onChange={(e) =>
                  setFormData({ ...formData, pendantSize: e.target.value })
                }
              />
            </div>
            <div className="erp-input-group" style={{ gridColumn: "span 2" }}>
              <label>Extra Remarks</label>
              <textarea
                rows="2"
                placeholder="Any additional information..."
                value={formData.remarks}
                onChange={(e) =>
                  setFormData({ ...formData, remarks: e.target.value })
                }
              />
            </div>
          </div>
        </div>

        <div className="modal-footer" style={{ justifyContent: "flex-end" }}>
          <button
            type="button"
            className="btn-cancel"
            style={{ flex: "none" }}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-save"
            style={{ flex: "none", margin: 0 }}
          >
            {currentStep < itemIndexes.length - 1
              ? "Next Item ➔"
              : itemTarget?.finalizationData
                ? "Update Metrics"
                : "Secure & Update"}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
};

// ==========================================
// 2. MODERN DETAIL MODAL (React Portal)
// ==========================================
const OrderDetailModal = ({
  order,
  onClose,
  onRefresh,
  onDelete,
  parties,
  onMasterStatusChange,
  onEditFinalDetails,
}) => {
  const isBulk = order.orderType !== "Single";
  const [activeTab, setActiveTab] = useState(isBulk ? "Details" : "Items");
  const [isUpdating, setIsUpdating] = useState(false);
  const [editData, setEditData] = useState(null);

  const [masterNameInput, setMasterNameInput] = useState(
    order.product || order.subType || "",
  );
  const [masterImgFile, setMasterImgFile] = useState(null);
  const [masterImgPreview, setMasterImgPreview] = useState("");

  const [isEditingBulkQty, setIsEditingBulkQty] = useState(false);
  const [newBulkQty, setNewBulkQty] = useState("");
  const [editingSingleQtyIdx, setEditingSingleQtyIdx] = useState(null);
  const [newSingleQty, setNewSingleQty] = useState("");

  const [newVar, setNewVar] = useState({
    weight: "",
    length: "",
    lengthUnit: "Inches",
    pendantWeight: "",
    pendantLength: "",
    pendantLengthUnit: "Inches",
    quantity: 1,
    file: null,
    preview: "",
  });
  const [bulkLog, setBulkLog] = useState({
    qty: "",
    weight: "",
    status: "Processing",
    note: "",
  });

  if (!order) return null;
  const partyName = getPartyName(order.partyId, parties);
  const isMasterDelivered = normalizeStatus(order.status) === "Delivered";

  let allImages = [];
  if (order.files) allImages.push(...order.files);
  if (order.singleItems) {
    order.singleItems.forEach((i) => {
      allImages.push(...extractAllItemImages(i));
    });
  }
  if (order.items) {
    order.items.forEach((i) => {
      allImages.push(...extractAllItemImages(i));
    });
  }
  allImages = [...new Set(allImages)];

  const handleEditClick = () => {
    setEditData({
      comments: order.comments || "",
      singleItems: order.singleItems
        ? JSON.parse(JSON.stringify(order.singleItems))
        : [],
      items: order.items ? JSON.parse(JSON.stringify(order.items)) : [],
    });
    setActiveTab("Edit");
  };

  const saveEdits = async () => {
    setIsUpdating(true);
    try {
      await updateDoc(doc(db, "partyOrders", order.id), {
        comments: editData.comments,
        singleItems: editData.singleItems,
        items: editData.items,
      });
      alert("Order Updated Successfully");
      setActiveTab("Items");
      onRefresh();
    } catch (e) {
      alert("Update failed");
    } finally {
      setIsUpdating(false);
    }
  };

  const saveMasterDetails = async () => {
    if (!masterNameInput && !masterImgFile) return alert("Nothing to update.");
    setIsUpdating(true);
    try {
      let updatePayload = {};
      if (order.orderType === "Single") updatePayload.product = masterNameInput;
      else updatePayload.subType = masterNameInput;

      if (masterImgFile) {
        const storageRef = ref(
          storage,
          `orders_images/${order.id}/master_${Date.now()}_${masterImgFile.name}`,
        );
        const snap = await uploadBytes(storageRef, masterImgFile);
        const url = await getDownloadURL(snap.ref);
        updatePayload.files = arrayUnion(url);
      }

      await updateDoc(doc(db, "partyOrders", order.id), updatePayload);
      alert("Master Details Updated Successfully");
      setMasterImgFile(null);
      setMasterImgPreview("");
      onRefresh();
    } catch (err) {
      alert("Update failed: " + err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateBulkQty = async () => {
    const qty = Number(newBulkQty);
    if (qty <= 0 || isNaN(qty)) return alert("Please enter a valid quantity.");
    setIsUpdating(true);
    try {
      await updateDoc(doc(db, "partyOrders", order.id), { totalQuantity: qty });
      setIsEditingBulkQty(false);
      onRefresh();
    } catch (e) {
      alert("Failed to update master quantity.");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateSingleQty = async (index) => {
    const qty = Number(newSingleQty);
    if (qty <= 0 || isNaN(qty)) return alert("Please enter a valid quantity.");
    setIsUpdating(true);
    try {
      const updatedItems = [...order.singleItems];
      updatedItems[index].quantity = qty;
      await updateDoc(doc(db, "partyOrders", order.id), {
        singleItems: updatedItems,
      });
      setEditingSingleQtyIdx(null);
      onRefresh();
    } catch (e) {
      alert("Failed to update item quantity.");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleItemStatusChange = async (index, newStatus) => {
    if (
      ["Complete", "Delivered"].includes(newStatus) &&
      !order.singleItems[index].finalizationData
    ) {
      onEditFinalDetails(order, index, newStatus);
      return;
    }

    setIsUpdating(true);
    try {
      const updatedItems = [...order.singleItems];
      updatedItems[index].itemStatus = newStatus;

      const allDelivered = updatedItems.every(
        (i) => normalizeStatus(i.itemStatus) === "Delivered",
      );
      const allComplete = updatedItems.every((i) =>
        ["Delivered", "Complete"].includes(normalizeStatus(i.itemStatus)),
      );

      let intendedMasterStatus = order.status;
      if (allDelivered) intendedMasterStatus = "Delivered";
      else if (allComplete && normalizeStatus(order.status) !== "Delivered")
        intendedMasterStatus = "Complete";

      if (
        intendedMasterStatus !== order.status &&
        ["Complete", "Delivered"].includes(intendedMasterStatus)
      ) {
        await updateDoc(doc(db, "partyOrders", order.id), {
          singleItems: updatedItems,
        });
        onMasterStatusChange(order, intendedMasterStatus);
      } else {
        await updateDoc(doc(db, "partyOrders", order.id), {
          singleItems: updatedItems,
          status: intendedMasterStatus,
        });
        onRefresh();
      }
    } catch (err) {
      alert("Status update failed");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAddVariation = async (e) => {
    e.preventDefault();
    if (!newVar.weight || !newVar.length)
      return alert("Weight and Length required.");
    setIsUpdating(true);
    try {
      let fileUrls = [];
      if (newVar.file) {
        const storageRef = ref(
          storage,
          `orders_images/${order.id}/var_${Date.now()}_${newVar.file.name}`,
        );
        const snap = await uploadBytes(storageRef, newVar.file);
        fileUrls.push(await getDownloadURL(snap.ref));
      }
      const variationObj = {
        ...newVar,
        itemStatus: "Pending",
        fileUrls,
        addedAt: new Date().toISOString(),
      };
      delete variationObj.file;
      delete variationObj.preview;

      await updateDoc(doc(db, "partyOrders", order.id), {
        singleItems: arrayUnion(variationObj),
        status: "Processing",
      });
      alert("Variation appended successfully!");
      setNewVar({
        weight: "",
        length: "",
        lengthUnit: "Inches",
        pendantWeight: "",
        pendantLength: "",
        pendantLengthUnit: "Inches",
        quantity: 1,
        file: null,
        preview: "",
      });
      setActiveTab("Items");
      onRefresh();
    } catch (err) {
      alert("Failed to add variation: " + err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const totalBulkQty =
    order.totalQuantity ||
    order.items?.reduce((acc, it) => acc + (Number(it.quantity) || 1), 0) ||
    1;
  const completedLogs = (order.productionLogs || []).filter((l) =>
    ["Complete", "Delivered", "Hallmarked"].includes(l.status),
  );
  const completedBulkQty = completedLogs.reduce(
    (acc, l) => acc + Number(l.qty || 0),
    0,
  );
  const totalLoggedWeight = (order.productionLogs || [])
    .reduce((acc, l) => acc + Number(l.weight || 0), 0)
    .toFixed(2);
  const remainingBulkQty = Math.max(0, totalBulkQty - completedBulkQty);
  const progressPct =
    Math.min(100, Math.round((completedBulkQty / totalBulkQty) * 100)) || 0;

  const handleAddBulkLog = async (e) => {
    e.preventDefault();
    if (!bulkLog.qty) return alert("Quantity is required.");
    setIsUpdating(true);
    try {
      const newLog = {
        id: Date.now(),
        date: new Date().toISOString(),
        qty: Number(bulkLog.qty),
        weight: bulkLog.weight,
        status: bulkLog.status,
        note: bulkLog.note,
      };
      let newMasterStatus = order.status;
      if (
        ["Complete", "Delivered", "Hallmarked"].includes(bulkLog.status) &&
        completedBulkQty + Number(bulkLog.qty) >= totalBulkQty
      ) {
        newMasterStatus = bulkLog.status;
      } else if (order.status === "Pending") newMasterStatus = "Processing";

      await updateDoc(doc(db, "partyOrders", order.id), {
        productionLogs: arrayUnion(newLog),
        status: newMasterStatus,
      });
      setBulkLog({ qty: "", weight: "", status: "Processing", note: "" });
      onRefresh();
    } catch (err) {
      alert("Failed to add production log.");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteBulkLog = async (logId) => {
    if (!window.confirm("Remove this production log?")) return;
    setIsUpdating(true);
    try {
      const updatedLogs = (order.productionLogs || []).filter(
        (l) => l.id !== logId,
      );
      await updateDoc(doc(db, "partyOrders", order.id), {
        productionLogs: updatedLogs,
      });
      onRefresh();
    } catch (err) {
      alert("Failed to delete log.");
    } finally {
      setIsUpdating(false);
    }
  };

  return createPortal(
    <div className="erp-modal-overlay">
      <div
        className="erp-modern-modal erp-pop-in"
        style={isBulk ? { maxWidth: "900px" } : {}}
      >
        <div className="modal-header">
          <div className="header-info">
            <h2>
              #{order.orderId} - {partyName}
            </h2>
            <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span className="badge">{order.orderType}</span> •{" "}
              {order.deliveryDate} •
              <select
                value={normalizeStatus(order.status)}
                onChange={(e) => onMasterStatusChange(order, e.target.value)}
                style={{
                  background: "rgba(0,0,0,0.2)",
                  color: isMasterDelivered ? "#10b981" : "#3b82f6",
                  border: "1px solid #334155",
                  borderRadius: "4px",
                  padding: "4px 8px",
                  fontWeight: "bold",
                  outline: "none",
                  cursor: "pointer",
                }}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
              {isBulk && order.finalizationData && (
                <button
                  onClick={() => onEditFinalDetails(order)}
                  style={{
                    background: "transparent",
                    border: "1px dashed #d97706",
                    color: "#d97706",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "0.8rem",
                    fontWeight: "600",
                  }}
                  title="Edit Finalization Metrics"
                >
                  ✏️ Edit Metrics
                </button>
              )}
            </span>
          </div>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-tabs">
          <button
            className={activeTab === "Items" ? "active" : ""}
            onClick={() => setActiveTab("Items")}
          >
            {isBulk ? "Bulk Production Tracker" : "Order Items"}
          </button>
          {isBulk && (
            <button
              className={activeTab === "Details" ? "active" : ""}
              onClick={() => setActiveTab("Details")}
            >
              Order Specs
            </button>
          )}
          {!isMasterDelivered && (
            <button
              className={activeTab === "Edit" ? "active" : ""}
              onClick={handleEditClick}
            >
              Edit & Track
            </button>
          )}
          {!isBulk && !isMasterDelivered && (
            <button
              className={activeTab === "AddVariation" ? "active" : ""}
              onClick={() => setActiveTab("AddVariation")}
            >
              + Appends
            </button>
          )}
        </div>

        <div className="modal-body">
          {activeTab === "Edit" && editData && (
            <div className="erp-fade-in">
              <div className="erp-edit-master-card">
                <h4 className="card-title">
                  ✏️ Update Order Name & Cover Image
                </h4>
                <div className="master-edit-grid">
                  <div className="erp-input-group">
                    <label>Product / Type Name</label>
                    <input
                      type="text"
                      value={masterNameInput}
                      onChange={(e) => setMasterNameInput(e.target.value)}
                    />
                  </div>
                  <div className="erp-input-group">
                    <label>Replace Cover Image</label>
                    <div className="file-upload-wrapper">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          if (e.target.files[0]) {
                            setMasterImgFile(e.target.files[0]);
                            setMasterImgPreview(
                              URL.createObjectURL(e.target.files[0]),
                            );
                          }
                        }}
                      />
                      {masterImgPreview && (
                        <img
                          src={masterImgPreview}
                          alt="Preview"
                          className="preview-mini"
                        />
                      )}
                    </div>
                  </div>
                </div>
                <button
                  className="btn-save-sm"
                  onClick={saveMasterDetails}
                  disabled={isUpdating}
                  style={{ marginTop: "16px" }}
                >
                  {isUpdating ? "Saving..." : "Save Master Details"}
                </button>
              </div>

              <div className="erp-edit-group" style={{ marginBottom: "20px" }}>
                <label>Instructions</label>
                <textarea
                  rows="3"
                  value={editData.comments}
                  onChange={(e) =>
                    setEditData({ ...editData, comments: e.target.value })
                  }
                  style={{ width: "100%" }}
                />
              </div>
              {order.orderType === "Single" &&
                editData.singleItems.map((item, idx) => (
                  <div
                    key={idx}
                    style={{
                      background: "#1e293b",
                      padding: "16px",
                      borderRadius: "8px",
                      marginBottom: "16px",
                    }}
                  >
                    <h4 style={{ margin: "0 0 12px 0", color: "#60a5fa" }}>
                      Item #{idx + 1}
                    </h4>
                    <div style={{ display: "flex", gap: "10px" }}>
                      <div className="erp-edit-group">
                        <label>Weight</label>
                        <input
                          type="text"
                          value={item.weight}
                          onChange={(e) => {
                            const n = [...editData.singleItems];
                            n[idx].weight = e.target.value;
                            setEditData({ ...editData, singleItems: n });
                          }}
                        />
                      </div>
                      <div className="erp-edit-group">
                        <label>Length</label>
                        <input
                          type="text"
                          value={item.length}
                          onChange={(e) => {
                            const n = [...editData.singleItems];
                            n[idx].length = e.target.value;
                            setEditData({ ...editData, singleItems: n });
                          }}
                        />
                      </div>
                      <div className="erp-edit-group">
                        <label>Qty</label>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => {
                            const n = [...editData.singleItems];
                            n[idx].quantity = e.target.value;
                            setEditData({ ...editData, singleItems: n });
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}

          {activeTab === "Items" && !isBulk && (
            <div>
              {allImages.length > 0 && (
                <div className="modal-image-gallery">
                  {allImages.map((img, i) => (
                    <a href={img} target="_blank" rel="noreferrer" key={i}>
                      <img src={img} alt="Ref" />
                    </a>
                  ))}
                </div>
              )}
              <div
                style={{
                  background: "#1e293b",
                  padding: "16px",
                  borderRadius: "8px",
                  marginBottom: "20px",
                }}
              >
                <h4 style={{ margin: "0 0 10px 0", color: "#3b82f6" }}>
                  Instructions
                </h4>
                <p style={{ margin: 0, color: "#f8fafc", lineHeight: "1.5" }}>
                  {order.comments || "No instructions provided."}
                </p>
              </div>

              {order.singleItems?.map((row, idx) => {
                const itemStat = normalizeStatus(row.itemStatus);
                const isItemDelivered = itemStat === "Delivered";
                const itemImages = extractAllItemImages(row);

                return (
                  <div
                    key={idx}
                    className={`erp-subitem-card ${isItemDelivered ? "delivered-sub" : ""}`}
                  >
                    <div className="sub-top">
                      {itemImages.length > 0 ? (
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            flexWrap: "wrap",
                            maxWidth: "150px",
                          }}
                        >
                          {itemImages.map((imgUrl, imgIdx) => (
                            <a
                              href={imgUrl}
                              target="_blank"
                              rel="noreferrer"
                              key={imgIdx}
                            >
                              <img
                                src={imgUrl}
                                alt={`Ref ${imgIdx + 1}`}
                                className="sub-img"
                                style={{
                                  width: "60px",
                                  height: "60px",
                                  objectFit: "cover",
                                  borderRadius: "4px",
                                }}
                              />
                            </a>
                          ))}
                        </div>
                      ) : (
                        <div
                          className="sub-img"
                          style={{
                            background: "#1e293b",
                            display: "grid",
                            placeItems: "center",
                            fontSize: "0.7rem",
                            width: "60px",
                            height: "60px",
                            borderRadius: "4px",
                          }}
                        >
                          No Img
                        </div>
                      )}

                      <div className="sub-info">
                        <p>
                          <strong>Item {idx + 1}:</strong> {row.weight}g |{" "}
                          {row.length} {row.lengthUnit}
                        </p>
                        {row.pendantWeight && (
                          <p>
                            <strong>Pendant:</strong> {row.pendantWeight}g |{" "}
                            {row.pendantLength} {row.pendantLengthUnit}
                          </p>
                        )}
                        {row.addedAt && (
                          <p style={{ fontSize: "0.75rem", color: "#f59e0b" }}>
                            Added Later
                          </p>
                        )}
                      </div>
                    </div>

                    {row.finalizationData && (
                      <div
                        className="erp-final-metrics-display fade-in"
                        style={{
                          padding: "12px",
                          margin: "10px 0",
                          background: "rgba(0,0,0,0.2)",
                          borderRadius: "8px",
                          border: "1px dashed #d97706",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "8px",
                          }}
                        >
                          <span
                            style={{
                              color: "#d97706",
                              fontWeight: "bold",
                              fontSize: "0.85rem",
                            }}
                          >
                            🏆 Final Specs
                          </span>
                          <button
                            onClick={() => onEditFinalDetails(order, idx)}
                            style={{
                              background: "transparent",
                              border: "none",
                              color: "#3b82f6",
                              cursor: "pointer",
                              fontSize: "0.85rem",
                              fontWeight: "bold",
                            }}
                          >
                            ✏️ Edit
                          </button>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: "16px",
                            flexWrap: "wrap",
                            fontSize: "0.85rem",
                            color: "#f8fafc",
                          }}
                        >
                          <div>
                            <span
                              style={{
                                color: "#94a3b8",
                                display: "block",
                                fontSize: "0.7rem",
                              }}
                            >
                              Gross
                            </span>
                            <strong>{row.finalizationData.grossWeight}</strong>
                          </div>
                          <div>
                            <span
                              style={{
                                color: "#94a3b8",
                                display: "block",
                                fontSize: "0.7rem",
                              }}
                            >
                              Net
                            </span>
                            <strong>{row.finalizationData.netWeight}</strong>
                          </div>
                          {row.finalizationData.bbWeight && (
                            <div>
                              <span
                                style={{
                                  color: "#94a3b8",
                                  display: "block",
                                  fontSize: "0.7rem",
                                }}
                              >
                                BB
                              </span>
                              <strong>{row.finalizationData.bbWeight}</strong>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="sub-bottom">
                      {editingSingleQtyIdx === idx ? (
                        <div
                          style={{
                            display: "flex",
                            gap: "4px",
                            alignItems: "center",
                          }}
                        >
                          <input
                            type="number"
                            value={newSingleQty}
                            onChange={(e) => setNewSingleQty(e.target.value)}
                            style={{
                              width: "50px",
                              padding: "2px",
                              borderRadius: "4px",
                              border: "1px solid #cbd5e1",
                            }}
                          />
                          <button
                            onClick={() => handleUpdateSingleQty(idx)}
                            style={{
                              background: "#10b981",
                              color: "#fff",
                              border: "none",
                              borderRadius: "4px",
                              padding: "2px 6px",
                              cursor: "pointer",
                            }}
                          >
                            ✓
                          </button>
                          <button
                            onClick={() => setEditingSingleQtyIdx(null)}
                            style={{
                              background: "#ef4444",
                              color: "#fff",
                              border: "none",
                              borderRadius: "4px",
                              padding: "2px 6px",
                              cursor: "pointer",
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <span
                          className="qty"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          {row.quantity} Pcs
                          <button
                            onClick={() => {
                              setEditingSingleQtyIdx(idx);
                              setNewSingleQty(row.quantity);
                            }}
                            style={{
                              background: "transparent",
                              border: "none",
                              cursor: "pointer",
                              fontSize: "0.8rem",
                              opacity: 0.6,
                            }}
                            title="Edit Quantity"
                          >
                            ✏️
                          </button>
                        </span>
                      )}
                      <select
                        value={itemStat}
                        onChange={(e) =>
                          handleItemStatusChange(idx, e.target.value)
                        }
                        disabled={isUpdating}
                      >
                        {STATUS_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === "Appends" && !isBulk && (
            <form
              className="erp-variation-form fade-in"
              onSubmit={handleAddVariation}
            >
              <h3 className="form-title">Append Same-Design Variation</h3>
              <div className="erp-input-group" style={{ marginBottom: "16px" }}>
                <label>Main Chain</label>
                <div style={{ display: "flex", gap: "10px" }}>
                  <input
                    type="text"
                    placeholder="Weight (e.g. 25g)"
                    value={newVar.weight}
                    onChange={(e) =>
                      setNewVar({ ...newVar, weight: e.target.value })
                    }
                    required
                  />
                  <input
                    type="text"
                    placeholder="Length (e.g. 30)"
                    value={newVar.length}
                    onChange={(e) =>
                      setNewVar({ ...newVar, length: e.target.value })
                    }
                    required
                  />
                </div>
              </div>
              <div className="erp-input-group" style={{ marginBottom: "16px" }}>
                <label>Pendant (Optional)</label>
                <div style={{ display: "flex", gap: "10px" }}>
                  <input
                    type="text"
                    placeholder="Pend Wt"
                    value={newVar.pendantWeight}
                    onChange={(e) =>
                      setNewVar({ ...newVar, pendantWeight: e.target.value })
                    }
                  />
                  <input
                    type="text"
                    placeholder="Pend Size"
                    value={newVar.pendantLength}
                    onChange={(e) =>
                      setNewVar({ ...newVar, pendantLength: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className="erp-input-group" style={{ marginBottom: "16px" }}>
                <label>Quantity</label>
                <input
                  type="number"
                  min="1"
                  value={newVar.quantity}
                  onChange={(e) =>
                    setNewVar({ ...newVar, quantity: e.target.value })
                  }
                  required
                />
              </div>
              <label className="upload-zone">
                {newVar.preview ? (
                  <img
                    src={newVar.preview}
                    alt="Preview"
                    style={{ height: "60px", borderRadius: "4px" }}
                  />
                ) : (
                  <span>+ Upload Specific Variation Image (Optional)</span>
                )}
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      setNewVar({
                        ...newVar,
                        file: e.target.files[0],
                        preview: URL.createObjectURL(e.target.files[0]),
                      });
                    }
                  }}
                />
              </label>
              <button
                type="submit"
                className="btn-submit"
                disabled={isUpdating}
              >
                {isUpdating ? "Saving..." : "Inject into Order Ticket"}
              </button>
            </form>
          )}

          {activeTab === "Details" && isBulk && (
            <div className="erp-bulk-details fade-in">
              <div className="detail-master-grid">
                {order.orderType === "Casting" ? (
                  <>
                    <div className="info-block">
                      <span className="lbl">Item</span>
                      <span className="val highlight">
                        {order.castingDetails?.item || "N/A"}
                      </span>
                    </div>
                    <div className="info-block">
                      <span className="lbl">Carat</span>
                      <span className="val">
                        {order.castingDetails?.carat || "N/A"}
                      </span>
                    </div>
                    <div className="info-block">
                      <span className="lbl">Est Weight</span>
                      <span className="val">
                        {order.castingDetails?.weight || "N/A"}
                      </span>
                    </div>
                    <div className="info-block">
                      <span className="lbl">Stone</span>
                      <span className="val">
                        {order.castingDetails?.stone || "N/A"}
                      </span>
                    </div>
                    <div className="info-block">
                      <span className="lbl">Budget</span>
                      <span className="val">
                        {order.castingDetails?.budget || "N/A"}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="info-block">
                      <span className="lbl">Product Category</span>
                      <span className="val">
                        {order.subType || order.product || "N/A"}
                      </span>
                    </div>
                    <div className="info-block">
                      <span className="lbl">Total Target Qty</span>
                      {isEditingBulkQty ? (
                        <div
                          style={{
                            display: "flex",
                            gap: "6px",
                            alignItems: "center",
                          }}
                        >
                          <input
                            type="number"
                            value={newBulkQty}
                            onChange={(e) => setNewBulkQty(e.target.value)}
                            style={{
                              width: "60px",
                              padding: "4px",
                              borderRadius: "4px",
                              border: "1px solid #cbd5e1",
                            }}
                          />
                          <button
                            onClick={handleUpdateBulkQty}
                            style={{
                              background: "#10b981",
                              color: "#fff",
                              border: "none",
                              borderRadius: "4px",
                              padding: "4px 8px",
                              cursor: "pointer",
                            }}
                          >
                            ✓
                          </button>
                          <button
                            onClick={() => setIsEditingBulkQty(false)}
                            style={{
                              background: "#ef4444",
                              color: "#fff",
                              border: "none",
                              borderRadius: "4px",
                              padding: "4px 8px",
                              cursor: "pointer",
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <span
                          className="val highlight"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          {totalBulkQty} Pcs{" "}
                          <button
                            onClick={() => {
                              setIsEditingBulkQty(true);
                              setNewBulkQty(totalBulkQty);
                            }}
                            style={{
                              background: "transparent",
                              border: "none",
                              cursor: "pointer",
                              fontSize: "1rem",
                              opacity: 0.7,
                            }}
                            title="Modify Quantity"
                          >
                            ✏️
                          </button>
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
              {order.comments && (
                <div
                  style={{
                    marginTop: "20px",
                    padding: "16px",
                    background: "#f8fafc",
                    borderLeft: "4px solid #3f51b5",
                    borderRadius: "4px",
                    color: "#0f172a",
                  }}
                >
                  <strong>Instructions:</strong> {order.comments}
                </div>
              )}
              {order.orderType === "Bulk" &&
                order.items &&
                order.items.length > 0 && (
                  <div
                    className="table-responsive border-box"
                    style={{ marginTop: "24px" }}
                  >
                    <table className="management-table">
                      <thead>
                        <tr>
                          <th>Product Details</th>
                          <th>Specs / Weights</th>
                          <th style={{ textAlign: "center" }}>Req. Qty</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.items.map((item, idx) => (
                          <tr key={idx}>
                            <td>
                              <strong>
                                {getBulkItemDetailString(item, order)}
                              </strong>
                              {item.remarks && (
                                <div
                                  style={{
                                    color: "#64748b",
                                    fontSize: "0.85rem",
                                    marginTop: "2px",
                                  }}
                                >
                                  {item.remarks}
                                </div>
                              )}
                            </td>
                            <td style={{ color: "#475569" }}>
                              {item.weight || item.grossWeight || "-"}
                            </td>
                            <td
                              style={{
                                fontWeight: "bold",
                                textAlign: "center",
                              }}
                            >
                              {item.quantity || item.qty || 1}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              {allImages.length > 0 && (
                <div style={{ marginTop: "24px" }}>
                  <h4>Design References</h4>
                  <div
                    style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}
                  >
                    {allImages.map((url, idx) => (
                      <a href={url} target="_blank" rel="noreferrer" key={idx}>
                        <img
                          src={url}
                          alt="Design Ref"
                          style={{
                            height: "100px",
                            width: "100px",
                            objectFit: "cover",
                            borderRadius: "8px",
                            border: "1px solid #cbd5e1",
                          }}
                        />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "Items" && isBulk && (
            <div className="erp-bulk-tracking fade-in">
              <div
                style={{
                  background: "#f8fafc",
                  padding: "20px",
                  borderRadius: "12px",
                  marginBottom: "24px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "12px",
                  }}
                >
                  <span
                    style={{
                      fontWeight: "800",
                      color: "#1e293b",
                      textTransform: "uppercase",
                      fontSize: "0.85rem",
                    }}
                  >
                    Master Production Progress
                  </span>
                  <span
                    style={{
                      fontWeight: "800",
                      color: progressPct >= 100 ? "#10b981" : "#3f51b5",
                      fontSize: "1.1rem",
                    }}
                  >
                    {completedBulkQty} / {totalBulkQty} ({progressPct}%)
                  </span>
                </div>
                <div
                  style={{
                    height: "12px",
                    background: "#e2e8f0",
                    borderRadius: "6px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${progressPct}%`,
                      height: "100%",
                      background: progressPct >= 100 ? "#10b981" : "#3f51b5",
                      transition: "width 0.4s ease",
                    }}
                  ></div>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "30px",
                    marginTop: "16px",
                    fontSize: "0.95rem",
                    color: "#475569",
                  }}
                >
                  <div>
                    <strong>Remaining Target:</strong> {remainingBulkQty} Pcs
                  </div>
                  <div>
                    <strong>Total Logged Weight:</strong> {totalLoggedWeight}g
                  </div>
                </div>
              </div>
              <form
                onSubmit={handleAddBulkLog}
                style={{
                  display: "flex",
                  gap: "12px",
                  marginBottom: "24px",
                  alignItems: "flex-end",
                  background: "#ffffff",
                  padding: "20px",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
                  flexWrap: "wrap",
                  color: "#0f172a",
                }}
              >
                <div className="erp-input-group" style={{ flex: "1 1 80px" }}>
                  <label>Qty Done</label>
                  <input
                    type="number"
                    min="1"
                    max={remainingBulkQty > 0 ? remainingBulkQty : undefined}
                    required
                    value={bulkLog.qty}
                    onChange={(e) =>
                      setBulkLog({ ...bulkLog, qty: e.target.value })
                    }
                    style={{
                      padding: "10px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                    }}
                  />
                </div>
                <div className="erp-input-group" style={{ flex: "1 1 100px" }}>
                  <label>Weight (g)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={bulkLog.weight}
                    onChange={(e) =>
                      setBulkLog({ ...bulkLog, weight: e.target.value })
                    }
                    style={{
                      padding: "10px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                    }}
                  />
                </div>
                <div
                  className="erp-input-group"
                  style={{ flex: "1.5 1 120px" }}
                >
                  <label>Batch Status</label>
                  <select
                    value={bulkLog.status}
                    onChange={(e) =>
                      setBulkLog({ ...bulkLog, status: e.target.value })
                    }
                    style={{
                      padding: "10px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                      background: "#fff",
                    }}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div className="erp-input-group" style={{ flex: "2 1 200px" }}>
                  <label>Note / Identification</label>
                  <input
                    type="text"
                    placeholder="e.g. 11-0 16 inch / Karigar A"
                    value={bulkLog.note}
                    onChange={(e) =>
                      setBulkLog({ ...bulkLog, note: e.target.value })
                    }
                    style={{
                      padding: "10px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                    }}
                  />
                </div>
                <button
                  type="submit"
                  className="btn-print"
                  disabled={isUpdating}
                  style={{
                    height: "44px",
                    padding: "0 24px",
                    whiteSpace: "nowrap",
                    borderRadius: "6px",
                  }}
                >
                  + Add Log
                </button>
              </form>
              <div className="table-responsive border-box">
                <table
                  className="management-table"
                  style={{ color: "#0f172a" }}
                >
                  <thead>
                    <tr>
                      <th>Timestamp</th>
                      <th style={{ textAlign: "center" }}>Qty</th>
                      <th style={{ textAlign: "right" }}>Weight</th>
                      <th style={{ textAlign: "center" }}>Status</th>
                      <th>Note</th>
                      <th style={{ textAlign: "center" }}>Act</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(order.productionLogs || []).length === 0 && (
                      <tr>
                        <td
                          colSpan="6"
                          style={{
                            textAlign: "center",
                            color: "#94a3b8",
                            padding: "30px",
                          }}
                        >
                          No production logs recorded yet. Start tracking above.
                        </td>
                      </tr>
                    )}
                    {(order.productionLogs || [])
                      .slice()
                      .reverse()
                      .map((log) => (
                        <tr key={log.id}>
                          <td style={{ color: "#64748b", fontSize: "0.85rem" }}>
                            {new Date(log.date).toLocaleString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </td>
                          <td
                            style={{
                              fontWeight: "800",
                              textAlign: "center",
                              color: "#0f172a",
                            }}
                          >
                            {log.qty} Pcs
                          </td>
                          <td
                            style={{
                              textAlign: "right",
                              fontVariantNumeric: "tabular-nums",
                            }}
                          >
                            {log.weight ? `${log.weight}g` : "-"}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            <span
                              className="badge"
                              style={{
                                background: ["Complete", "Delivered"].includes(
                                  log.status,
                                )
                                  ? "#d1fae5"
                                  : "#e0e7ff",
                                color: ["Complete", "Delivered"].includes(
                                  log.status,
                                )
                                  ? "#047857"
                                  : "#3730a3",
                                border: "none",
                                padding: "4px 8px",
                              }}
                            >
                              {log.status}
                            </span>
                          </td>
                          <td style={{ color: "#475569" }}>
                            {log.note || "-"}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            <button
                              onClick={() => handleDeleteBulkLog(log.id)}
                              style={{
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                fontSize: "1.1rem",
                              }}
                              title="Delete Log"
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          {activeTab === "Edit" ? (
            <>
              <button
                className="btn-secondary"
                onClick={() => setActiveTab("Items")}
              >
                Cancel
              </button>
              <button
                className="btn-save"
                onClick={saveEdits}
                disabled={isUpdating}
              >
                {isUpdating ? "Saving..." : "Save Changes"}
              </button>
            </>
          ) : (
            <>
              <button
                className="btn-whatsapp"
                onClick={() => openWhatsAppReport(order, partyName)}
              >
                💬 WhatsApp
              </button>
              <button
                className="btn-print"
                onClick={() => triggerPrintJobCard(order, partyName)}
              >
                🖨️ Job Card
              </button>
              <button
                className="btn-delete"
                onClick={() => {
                  onDelete(order);
                  onClose();
                }}
              >
                🗑️ Delete
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

// ==========================================
// 3. ORDER CARD (Grid Item)
// ==========================================
const getDeliveryAlert = (dateStr) => {
  if (!dateStr) return { level: "safe", text: "No Date", days: 999 };
  const due = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffTime = due - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  if (diffDays < 0)
    return {
      level: "overdue",
      text: `Overdue by ${Math.abs(diffDays)}d`,
      days: diffDays,
    };
  if (diffDays === 0)
    return { level: "urgent", text: "Due Today!", days: diffDays };
  if (diffDays <= 2)
    return { level: "urgent", text: `Due in ${diffDays}d`, days: diffDays };
  if (diffDays <= 5)
    return { level: "warning", text: `Due in ${diffDays}d`, days: diffDays };
  return { level: "safe", text: dateStr, days: diffDays };
};

const OrderCard = ({
  order,
  isActive,
  onSelect,
  parties,
  onDirectStatusChange,
  isSelectedForBulk,
  onToggleBulkSelect,
}) => {
  if (!order) return null;
  const partyName = getPartyName(order.partyId, parties);
  const status = normalizeStatus(order.status);
  const isDelivered = status === "Delivered";
  const thumbnail = extractMasterThumbnail(order);
  const unseenCount = order.unseenCount || 0;

  const isUnread = !order.isViewed;

  let displayQtyInfo = "";
  if (order.orderType === "Single") {
    const tq = order.singleItems?.reduce(
      (sum, r) => sum + (Number(r.quantity) || 1),
      0,
    );
    displayQtyInfo = `${tq} Qty`;
  } else {
    const tq =
      order.totalQuantity ||
      order.items?.reduce(
        (sum, r) => sum + (Number(r.quantity || r.qty) || 1),
        0,
      ) ||
      1;
    const completed = (order.productionLogs || [])
      .filter((l) => ["Complete", "Delivered", "Hallmarked"].includes(l.status))
      .reduce((acc, l) => acc + Number(l.qty || 0), 0);
    displayQtyInfo = `${completed}/${tq} Done`;
  }

  const alert = getDeliveryAlert(order.deliveryDate);

  return (
    <div
      className={`erp-order-card ${isDelivered ? "locked-card" : ""} ${isActive ? "active-card" : ""} ${isSelectedForBulk ? "selected-for-bulk" : ""}`}
    >
      {isUnread && !isDelivered && (
        <div className="new-beacon-wrapper">
          <span className="beacon-dot"></span> New
        </div>
      )}

      <div className="bulk-checkbox" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={isSelectedForBulk}
          onChange={(e) => onToggleBulkSelect(order.id, e.target.checked)}
        />
      </div>

      <div className="hover-popover">
        {thumbnail && <img src={thumbnail} alt="Preview" className="pop-img" />}
        <div className="pop-info">
          <div>
            <strong>Order:</strong> #{order.orderId}
          </div>
          <div>
            <strong>Type:</strong> {order.product || order.orderType}
          </div>
          {order.orderType === "Single" && order.singleItems && (
            <div
              style={{
                marginTop: "8px",
                paddingTop: "8px",
                borderTop: "1px dashed #334155",
              }}
            >
              {order.singleItems.slice(0, 2).map((i, idx) => (
                <div key={idx}>
                  • {i.weight}g | {i.length}
                  {i.lengthUnit}
                </div>
              ))}
              {order.singleItems.length > 2 && (
                <div>+ {order.singleItems.length - 2} more items</div>
              )}
            </div>
          )}
          {order.orderType === "Bulk" && order.items && (
            <div
              style={{
                marginTop: "8px",
                paddingTop: "8px",
                borderTop: "1px dashed #334155",
              }}
            >
              {order.items.slice(0, 3).map((i, idx) => (
                <div key={idx}>
                  • {i.beadSize ? `${i.beadSize} - ${i.length}` : i.weightRange}{" "}
                  ({i.quantity || i.qty} pcs)
                </div>
              ))}
              {order.items.length > 3 && (
                <div>+ {order.items.length - 3} more items</div>
              )}
            </div>
          )}
          {order.orderType === "Casting" && order.castingDetails && (
            <div
              style={{
                marginTop: "8px",
                paddingTop: "8px",
                borderTop: "1px dashed #334155",
              }}
            >
              <div>• Item: {order.castingDetails.item}</div>
              <div>
                • Wt: {order.castingDetails.weight} (
                {order.castingDetails.carat})
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="card-thumb" onClick={() => onSelect(order)}>
        {thumbnail ? (
          <img src={thumbnail} alt="Item" />
        ) : (
          <div className="no-img">No Image</div>
        )}
        <div className="card-actions-overlay">
          <button
            className="chat-btn"
            onClick={(e) => {
              e.stopPropagation();
              window.alert(`Open Chat for ${order.orderId}`);
            }}
            title="View Chat"
          >
            💬 {unseenCount > 0 && <span className="badge">{unseenCount}</span>}
          </button>
        </div>
      </div>

      <div className="card-body" onClick={() => onSelect(order)}>
        <div className="card-row">
          <span className="party-name">{partyName}</span>
          <span className="order-id">#{order.orderId}</span>
        </div>
        <div className="card-row">
          <span className="type-badge">
            {order.orderType} • {order.product || "Item"}
          </span>
          <span
            className="summary"
            style={{
              color: order.orderType !== "Single" ? "#3b82f6" : "#f8fafc",
              fontWeight: order.orderType !== "Single" ? "bold" : "normal",
            }}
          >
            {displayQtyInfo}
          </span>
        </div>
      </div>

      <div className="card-footer" onClick={(e) => e.stopPropagation()}>
        <div
          className={`date-badge ${alert.level}`}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            padding: "4px 10px",
            borderRadius: "12px",
            fontSize: "0.75rem",
            fontWeight: "bold",
            background:
              alert.level === "overdue"
                ? "rgba(239,68,68,0.1)"
                : alert.level === "urgent"
                  ? "rgba(245,158,11,0.1)"
                  : "transparent",
            color:
              alert.level === "overdue"
                ? "#ef4444"
                : alert.level === "urgent"
                  ? "#f59e0b"
                  : "#94a3b8",
            border: `1px solid ${alert.level === "overdue" ? "rgba(239,68,68,0.3)" : alert.level === "urgent" ? "rgba(245,158,11,0.3)" : "transparent"}`,
          }}
        >
          {alert.text}
        </div>
        <select
          className="direct-status"
          value={status}
          onChange={(e) => onDirectStatusChange(order, e.target.value)}
          disabled={isDelivered}
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

// ==========================================
// 4. MAIN DASHBOARD
// ==========================================
const PartyOrderList = () => {
  const [orders, setOrders] = useState([]);
  const [parties, setParties] = useState([]);

  const [selectedOrder, setSelectedOrder] = useState(null);

  const [orderToFinalize, setOrderToFinalize] = useState(null);
  const [bulkFinalizeQueue, setBulkFinalizeQueue] = useState([]);

  const [selectedBulkOrders, setSelectedBulkOrders] = useState([]);

  const [activeType, setActiveType] = useState("Single");
  const [filterParty, setFilterParty] = useState("All");
  const [deliveredLimit, setDeliveredLimit] = useState(12);

  const fetchData = useCallback(async () => {
    const pSnap = await getDocs(query(collection(db, "parties")));
    setParties(pSnap.docs.map((d) => ({ id: d.id, name: d.data().name })));
    const oSnap = await getDocs(
      query(collection(db, "partyOrders"), orderBy("createdAt", "desc")),
    );
    setOrders(oSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (selectedOrder || orderToFinalize) {
      document.body.style.overflow = "hidden";
      const scrollableDiv =
        document.querySelector(".main-workspace") ||
        document.querySelector(".erp-main-workspace");
      if (scrollableDiv) scrollableDiv.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
      const scrollableDiv =
        document.querySelector(".main-workspace") ||
        document.querySelector(".erp-main-workspace");
      if (scrollableDiv) scrollableDiv.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "unset";
      const scrollableDiv =
        document.querySelector(".main-workspace") ||
        document.querySelector(".erp-main-workspace");
      if (scrollableDiv) scrollableDiv.style.overflow = "auto";
    };
  }, [selectedOrder, orderToFinalize]);

  const handleOrderSelect = async (order) => {
    setSelectedOrder(order);
    if (!order.isViewed) {
      setOrders((prev) =>
        prev.map((o) => (o.id === order.id ? { ...o, isViewed: true } : o)),
      );
      await updateDoc(doc(db, "partyOrders", order.id), { isViewed: true });
    }
  };

  const handleToggleBulkSelect = (orderId, isChecked) => {
    setSelectedBulkOrders((prev) =>
      isChecked ? [...prev, orderId] : prev.filter((id) => id !== orderId),
    );
  };

  const executeBulkStatusUpdate = async () => {
    const newStatus = document.getElementById("bulk-status-select").value;
    if (!newStatus) return;

    const ordersToProcess = orders.filter((o) =>
      selectedBulkOrders.includes(o.id),
    );
    const queue = [];
    const readyToUpdate = [];

    if (["Complete", "Delivered"].includes(newStatus)) {
      ordersToProcess.forEach((order) => {
        if (order.orderType === "Single") {
          const unfinalized = [];
          (order.singleItems || []).forEach((item, idx) => {
            if (!item.finalizationData) unfinalized.push(idx);
          });
          if (unfinalized.length > 0) {
            queue.push({
              orderObj: order,
              intendedMasterStatus: newStatus,
              intendedItemStatus: newStatus,
              itemIndexes: unfinalized,
            });
          } else {
            readyToUpdate.push(order);
          }
        } else {
          if (!order.finalizationData) {
            queue.push({
              orderObj: order,
              intendedMasterStatus: newStatus,
              intendedItemStatus: null,
              itemIndexes: [-1],
            });
          } else {
            readyToUpdate.push(order);
          }
        }
      });
    } else {
      readyToUpdate.push(...ordersToProcess);
    }

    if (readyToUpdate.length > 0) {
      await Promise.all(
        readyToUpdate.map((o) =>
          updateDoc(doc(db, "partyOrders", o.id), { status: newStatus }),
        ),
      );
    }

    if (queue.length > 0) {
      setBulkFinalizeQueue(queue.slice(1));
      setOrderToFinalize(queue[0]);
    } else {
      setSelectedBulkOrders([]);
      fetchData();
    }
  };

  const handleDirectStatusChange = async (order, newStatus) => {
    if (["Complete", "Delivered"].includes(newStatus)) {
      if (order.orderType === "Single") {
        const unfinalized = [];
        (order.singleItems || []).forEach((item, idx) => {
          if (!item.finalizationData) unfinalized.push(idx);
        });

        if (unfinalized.length > 0) {
          setOrderToFinalize({
            orderObj: order,
            intendedMasterStatus: newStatus,
            intendedItemStatus: newStatus,
            itemIndexes: unfinalized,
          });
          return;
        }
      } else {
        if (!order.finalizationData) {
          setOrderToFinalize({
            orderObj: order,
            intendedMasterStatus: newStatus,
            intendedItemStatus: null,
            itemIndexes: [-1],
          });
          return;
        }
      }
    }
    await updateDoc(doc(db, "partyOrders", order.id), { status: newStatus });
    fetchData();
  };

  const handleEditFinalDetails = (order, itemIndex = null) => {
    if (order.orderType === "Single") {
      if (itemIndex !== null) {
        setOrderToFinalize({
          orderObj: order,
          intendedMasterStatus: null,
          intendedItemStatus: null,
          itemIndexes: [itemIndex],
        });
      } else {
        const allIdx = (order.singleItems || []).map((_, i) => i);
        setOrderToFinalize({
          orderObj: order,
          intendedMasterStatus: null,
          intendedItemStatus: null,
          itemIndexes: allIdx.length > 0 ? allIdx : [0],
        });
      }
    } else {
      setOrderToFinalize({
        orderObj: order,
        intendedMasterStatus: null,
        intendedItemStatus: null,
        itemIndexes: [-1],
      });
    }
  };

  const submitFinalization = async (
    orderId,
    intendedMasterStatus,
    intendedItemStatus,
    collectedDataMap,
    itemIndexes,
  ) => {
    const order = orders.find((o) => o.id === orderId);
    let updatePayload = {};

    if (order.orderType === "Single") {
      const updatedItems = [...order.singleItems];
      itemIndexes.forEach((idx) => {
        if (collectedDataMap[idx])
          updatedItems[idx].finalizationData = collectedDataMap[idx];
        if (intendedItemStatus)
          updatedItems[idx].itemStatus = intendedItemStatus;
      });
      updatePayload.singleItems = updatedItems;

      if (
        intendedMasterStatus &&
        ["Complete", "Delivered"].includes(intendedMasterStatus)
      ) {
        updatePayload.status = intendedMasterStatus;
      } else {
        const allDel = updatedItems.every(
          (i) => normalizeStatus(i.itemStatus) === "Delivered",
        );
        const allComp = updatedItems.every((i) =>
          ["Delivered", "Complete"].includes(normalizeStatus(i.itemStatus)),
        );
        if (allDel) updatePayload.status = "Delivered";
        else if (allComp && order.status !== "Delivered")
          updatePayload.status = "Complete";
      }
    } else {
      updatePayload.finalizationData = collectedDataMap[-1];
      if (intendedMasterStatus) updatePayload.status = intendedMasterStatus;
    }

    await updateDoc(doc(db, "partyOrders", orderId), updatePayload);

    if (bulkFinalizeQueue.length > 0) {
      const nextOrderData = bulkFinalizeQueue[0];
      setBulkFinalizeQueue((prev) => prev.slice(1));
      setOrderToFinalize(nextOrderData);
    } else {
      setOrderToFinalize(null);
      setSelectedBulkOrders([]);
      fetchData();
    }
  };

  const closeFinalizeModal = () => {
    setOrderToFinalize(null);
    setBulkFinalizeQueue([]);
    setSelectedBulkOrders([]);
  };

  const handleDeleteOrder = async (order) => {
    if (!window.confirm(`PERMANENTLY delete Order #${order.orderId}?`)) return;
    await deleteDoc(doc(db, "partyOrders", order.id));
    setOrders((prev) => prev.filter((o) => o.id !== order.id));
  };

  const unreadCounts = { Single: 0, Bulk: 0, Casting: 0 };
  orders.forEach((o) => {
    if (!o.isViewed && normalizeStatus(o.status) !== "Delivered") {
      unreadCounts[o.orderType] = (unreadCounts[o.orderType] || 0) + 1;
    }
  });

  const filteredOrders = orders.filter((o) => {
    const matchType = o.orderType === activeType;
    const matchParty = filterParty === "All" || o.partyId === filterParty;
    return matchType && matchParty;
  });

  const groupedOrders = {
    actionNeeded: filteredOrders.filter((o) =>
      ["Pending"].includes(normalizeStatus(o.status)),
    ),
    inProduction: filteredOrders.filter((o) =>
      ["Processing", "Polishing"].includes(normalizeStatus(o.status)),
    ),
    ready: filteredOrders.filter((o) =>
      ["Complete", "Hallmarked"].includes(normalizeStatus(o.status)),
    ),
    delivered: filteredOrders.filter((o) =>
      ["Delivered"].includes(normalizeStatus(o.status)),
    ),
  };

  const partySummaries = parties
    .map((p) => {
      const pOrders = orders.filter(
        (o) =>
          o.partyId === p.id &&
          !["Delivered"].includes(normalizeStatus(o.status)),
      );
      const pending = pOrders.filter((o) =>
        ["Pending", "Processing", "Polishing"].includes(
          normalizeStatus(o.status),
        ),
      ).length;
      const ready = pOrders.filter((o) =>
        ["Complete", "Hallmarked"].includes(normalizeStatus(o.status)),
      ).length;
      return { ...p, pending, ready, total: pending + ready };
    })
    .filter((p) => p.total > 0)
    .sort((a, b) => b.total - a.total);

  return (
    <div className="erp-order-list-wrapper">
      {selectedBulkOrders.length > 0 && (
        <div className="bulk-action-bar erp-fade-up">
          <span className="selected-count">
            {selectedBulkOrders.length} Orders Selected
          </span>
          <select id="bulk-status-select" defaultValue="">
            <option value="" disabled>
              -- Update Status --
            </option>
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          <button className="btn-apply" onClick={executeBulkStatusUpdate}>
            Apply Changes
          </button>
          <button
            className="btn-cancel"
            onClick={() => setSelectedBulkOrders([])}
          >
            ✕ Clear
          </button>
        </div>
      )}

      {selectedOrder && (
        <OrderDetailModal
          order={orders.find((o) => o.id === selectedOrder.id)}
          parties={parties}
          onClose={() => setSelectedOrder(null)}
          onRefresh={fetchData}
          onDelete={handleDeleteOrder}
          onMasterStatusChange={handleDirectStatusChange}
          onEditFinalDetails={handleEditFinalDetails}
        />
      )}

      {orderToFinalize && (
        <FinalizeOrderModal
          order={orderToFinalize.orderObj}
          partyName={getPartyName(orderToFinalize.orderObj.partyId, parties)}
          intendedMasterStatus={orderToFinalize.intendedMasterStatus}
          intendedItemStatus={orderToFinalize.intendedItemStatus}
          itemIndexes={orderToFinalize.itemIndexes}
          onClose={closeFinalizeModal}
          onSaveMultiple={submitFinalization}
        />
      )}

      <div className="erp-page-header">
        <div className="header-left">
          <h1>Production Dashboard</h1>
          <p>
            Monitor live statuses, finalize weights, and collaborate with
            parties.
          </p>
        </div>
        <button
          className="btn-print-list"
          onClick={() => triggerPrintPendingList(orders, parties)}
        >
          🖨️ Print Pending Orders (A4)
        </button>
      </div>

      {partySummaries.length > 0 && (
        <div className="erp-party-summary-scroll">
          {partySummaries.map((p) => (
            <div key={p.id} className="party-summary-card erp-fade-up">
              <div className="ps-name">{p.name}</div>
              <div className="ps-stats">
                <div className="stat pending">
                  <span className="lbl">In Work</span>
                  <span className="val">{p.pending}</span>
                </div>
                <div className="stat ready">
                  <span className="lbl">Ready</span>
                  <span className="val">{p.ready}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="erp-controls-card erp-fade-up">
        <div className="top-row">
          {["Single", "Bulk", "Casting"].map((type) => (
            <button
              key={type}
              className={activeType === type ? "active" : ""}
              onClick={() => {
                setActiveType(type);
                setSelectedOrder(null);
                setDeliveredLimit(12);
              }}
            >
              {type} Orders
              {unreadCounts[type] > 0 && (
                <span className="tab-badge">{unreadCounts[type]} New</span>
              )}
            </button>
          ))}
        </div>
        <div className="erp-input-group">
          <label>Filter by Master Party</label>
          <select
            value={filterParty}
            onChange={(e) => setFilterParty(e.target.value)}
          >
            <option value="All">All Active Accounts</option>
            {parties.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {groupedOrders.actionNeeded.length > 0 && (
        <div className="erp-status-group">
          <div className="group-header">
            🛑 Action Required{" "}
            <span className="count-badge">
              {groupedOrders.actionNeeded.length}
            </span>
          </div>
          <div className="erp-orders-grid">
            {groupedOrders.actionNeeded.map((o) => (
              <OrderCard
                key={o.id}
                order={o}
                parties={parties}
                isActive={selectedOrder?.id === o.id}
                onSelect={handleOrderSelect}
                onDirectStatusChange={handleDirectStatusChange}
                isSelectedForBulk={selectedBulkOrders.includes(o.id)}
                onToggleBulkSelect={handleToggleBulkSelect}
              />
            ))}
          </div>
        </div>
      )}

      {groupedOrders.inProduction.length > 0 && (
        <div className="erp-status-group">
          <div className="group-header">
            ⚙️ In Production{" "}
            <span className="count-badge" style={{ background: "#0ea5e9" }}>
              {groupedOrders.inProduction.length}
            </span>
          </div>
          <div className="erp-orders-grid">
            {groupedOrders.inProduction.map((o) => (
              <OrderCard
                key={o.id}
                order={o}
                parties={parties}
                isActive={selectedOrder?.id === o.id}
                onSelect={handleOrderSelect}
                onDirectStatusChange={handleDirectStatusChange}
                isSelectedForBulk={selectedBulkOrders.includes(o.id)}
                onToggleBulkSelect={handleToggleBulkSelect}
              />
            ))}
          </div>
        </div>
      )}

      {groupedOrders.ready.length > 0 && (
        <div className="erp-status-group">
          <div className="group-header">
            ✅ Ready to Deliver{" "}
            <span className="count-badge" style={{ background: "#10b981" }}>
              {groupedOrders.ready.length}
            </span>
          </div>
          <div className="erp-orders-grid">
            {groupedOrders.ready.map((o) => (
              <OrderCard
                key={o.id}
                order={o}
                parties={parties}
                isActive={selectedOrder?.id === o.id}
                onSelect={handleOrderSelect}
                onDirectStatusChange={handleDirectStatusChange}
                isSelectedForBulk={selectedBulkOrders.includes(o.id)}
                onToggleBulkSelect={handleToggleBulkSelect}
              />
            ))}
          </div>
        </div>
      )}

      {groupedOrders.delivered.length > 0 && (
        <div className="erp-status-group" style={{ opacity: 0.8 }}>
          <div className="group-header" style={{ color: "#64748b" }}>
            📦 Delivered History{" "}
            <span className="count-badge" style={{ background: "#334155" }}>
              {groupedOrders.delivered.length}
            </span>
          </div>
          <div className="erp-orders-grid">
            {groupedOrders.delivered.slice(0, deliveredLimit).map((o) => (
              <OrderCard
                key={o.id}
                order={o}
                parties={parties}
                isActive={selectedOrder?.id === o.id}
                onSelect={handleOrderSelect}
                onDirectStatusChange={handleDirectStatusChange}
                isSelectedForBulk={selectedBulkOrders.includes(o.id)}
                onToggleBulkSelect={handleToggleBulkSelect}
              />
            ))}
          </div>
          {groupedOrders.delivered.length > deliveredLimit && (
            <div className="load-more-container">
              <button
                className="btn-load-more"
                onClick={() => setDeliveredLimit((prev) => prev + 12)}
              >
                Load More Delivered
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PartyOrderList;
